import { useEffect, useState, useCallback } from "react";
import { Trash2, MessageCircle, Phone, RefreshCcw, ClipboardList, CheckCircle2, Users, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { waLink } from "@/lib/brand";

const STATUS_OPTS = ["new", "contacted", "enrolled", "closed"];
const STATUS_COLOR = {
  new: "bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/30",
  contacted: "bg-blue-100 text-blue-700 border-blue-200",
  enrolled: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#0B192C] grid place-items-center"><Icon size={18} className="text-[#FF6B35]" /></div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="mt-4 font-display text-4xl font-800 text-[#0B192C]">{value}</div>
    </div>
  );
}

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        api.get(`/admin/enquiries${filter !== "all" ? `?status=${filter}` : ""}`),
        api.get("/admin/stats"),
      ]);
      setEnquiries(e.data || []);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/enquiries.csv${filter !== "all" ? `?status=${filter}` : ""}`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `vikramshila-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-800 text-[#0B192C]">Admission Enquiries</h1>
          <p className="text-slate-600 mt-1">All leads from the website — manage them here.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTS.map(s => (<SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>))}
            </SelectContent>
          </Select>
          <button onClick={exportCsv} className="vk-btn-primary text-sm" data-testid="export-csv-btn"><Download size={14} /> Export CSV</button>
          <button onClick={load} className="vk-btn-dark text-sm" data-testid="refresh-btn"><RefreshCcw size={14} /> Refresh</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatTile icon={Users} label="Total" value={stats.total} />
          <StatTile icon={ClipboardList} label="New" value={stats.new} />
          <StatTile icon={MessageCircle} label="Contacted" value={stats.contacted} />
          <StatTile icon={CheckCircle2} label="Enrolled" value={stats.enrolled} />
          <StatTile icon={Trash2} label="Closed" value={stats.closed} />
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enquiries-table">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Student</th>
                <th className="text-left px-4 py-3">Class · Board</th>
                <th className="text-left px-4 py-3">Parent</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Subjects</th>
                <th className="text-left px-4 py-3">Received</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={8} className="text-center px-4 py-10 text-slate-500">Loading…</td></tr>)}
              {!loading && enquiries.length === 0 && (<tr><td colSpan={8} className="text-center px-4 py-10 text-slate-500">No enquiries yet.</td></tr>)}
              {enquiries.map((e) => (
                <tr key={e.id} data-testid={`enquiry-row-${e.id}`}>
                  <td className="px-4 py-4 font-medium text-[#0B192C]">{e.student_name}</td>
                  <td className="px-4 py-4"><span className="vk-chip text-xs">Class {e.student_class} · {e.board}</span></td>
                  <td className="px-4 py-4 text-slate-700">{e.parent_name}</td>
                  <td className="px-4 py-4 text-slate-700">
                    <div className="flex items-center gap-3">
                      <a href={`tel:${e.phone}`} className="hover:text-[#FF6B35]" title="Call"><Phone size={14} /></a>
                      <a href={waLink(`Hi ${e.parent_name}, this is Vikramshila Classes.`)} target="_blank" rel="noreferrer" className="hover:text-[#25D366]" title="WhatsApp"><MessageCircle size={14} /></a>
                      <span>{e.phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-slate-700 max-w-[220px] truncate" title={e.subjects}>{e.subjects}</td>
                  <td className="px-4 py-4 text-slate-500 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                      <SelectTrigger className={`w-32 border ${STATUS_COLOR[e.status] || ""}`} data-testid={`status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTS.map(s => (<SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => remove(e.id)} data-testid={`delete-${e.id}`} className="text-slate-400 hover:text-red-500 transition-colors" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
